import React, { Suspense } from "react";
import { Outlet, RouteObject } from "react-router";
import App from "@/App";
import { LazyLoadErrorFallback } from "@/components/LazyLoadErrorFallback";
import { Loading } from "@/components/Loading";
import { isAgentBoardEnabled } from "@/configs/agentFeatures";
import { URLs } from "@/configs/menu";
import Layout from "@/layout/v4";
import { isAgentsDemoMode } from "@/libs/agentsMode";
import TenantEnforcementBridge from "@/libs/TenantEnforcementBridge";
import {
  agentFeatureRoutes,
  featureFlaggedRoutes,
} from "@/Routes/featureFlagged";
import {
  type MockScopedConfig,
  mockScopedRoutes,
  mockScopedSwapRoute,
} from "@/Routes/mockScoped";
import { ApplicationType } from "@/types/common";
import {
  publicRoute,
  publicRouteWithProps,
  authRoute,
  authRouteWithProps,
  authRedirectRoute,
  createLazyRoute,
  replaceFirstSlash,
} from "./helper";
import { rootRouteLoader, requireAuthLoader } from "./loaders";

/**
 * The new agent experience: mounted only once the tenant's saved enforcement
 * has been read from the real API, and mock-scoped only in demo mode
 * (`isAgentsMock`) - otherwise every call is live. See `@/libs/agentsMode`.
 */
const AGENT_ONBOARDING: MockScopedConfig = {
  flag: "isNewAgentOnboarding",
  wrapper: TenantEnforcementBridge,
  mockWhen: () => isAgentsDemoMode(),
};

/**
 * The board replaces the legacy dashboard. `isAgentBoardEnabled` is the flag
 * alone today, so `enabledWhen` adds no gate of its own; it is kept so the
 * route, the default landing path and the app shell read one predicate and
 * keep agreeing if the board ever needs more than the flag.
 */
const AGENT_BOARD: MockScopedConfig = {
  ...AGENT_ONBOARDING,
  enabledWhen: () => isAgentBoardEnabled(),
};

const accountDetailPaths = [
  URLs.account_details,
  URLs.account_details_type,
  URLs.account_details_type_with_children,
];
const userAttributePaths = [URLs.users_attributes, URLs.users_attributes_type];
const userReportPaths = [URLs.user_reports, URLs.user_reports_type];
const configUdfPaths = [
  URLs.configurations_user_defined_functions,
  URLs.configurations_user_defined_functions_type,
];
const legacyConfigUdfPaths = [
  URLs.legacy_configurations_user_defined_functions,
  URLs.legacy_configurations_user_defined_functions_type,
];

/**
 * Redirect a legacy route to its canonical replacement, substituting the
 * `:type` param into the canonical typed URL when the legacy route carried one.
 */
const redirectToTypedPath = (
  legacyPath: string,
  canonicalBase: string,
  canonicalTyped: string,
) =>
  authRedirectRoute(legacyPath, params =>
    params.type ? canonicalTyped.replace(":type", params.type) : canonicalBase,
  );
const domainDataDomainPaths = [
  URLs.domain_management_data_domain,
  URLs.domain_management_data_domain_type,
];
const domainDataProductPaths = [
  URLs.domain_management_data_product,
  URLs.domain_management_data_product_type,
];
const resourcePolicyPaths = [
  URLs.resource_policies,
  URLs.resource_policies_type,
  URLs.resource_policy_details_type_with_children,
];
const aiAgentResourcePolicyPaths = [
  URLs.ai_agents_resource_policies,
  URLs.ai_agents_resource_policies_type,
  URLs.ai_agents_resource_policy_details_type_with_children,
];

const mcpRegistryPaths = [URLs.mcp, URLs.mcp_type];
const mcpServerDetailsPaths = [
  URLs.mcp_server_details,
  URLs.mcp_server_details_type,
];
const agentEstateDetailPaths = [
  URLs.agent_estate_details,
  URLs.agent_estate_details_type,
];

const unifiedLakehousePaths = [
  URLs.unified_lakehouse,
  URLs.unified_lakehouse_type,
];

const enterprisePolicyDetailPaths = [
  URLs.enterprise_policy_details,
  URLs.enterprise_policy_details_type,
];

const reportsPagePaths = [URLs.reports, URLs.reports_tab];

const publicRoutes: RouteObject[] = [
  publicRoute("login", () => import("@/pages/login")),
  publicRoute(URLs.aws_singUp, () => import("@/pages/SignUp")),
  publicRouteWithProps(URLs.free_trial, () => import("@/pages/SignUp"), {
    type: "free_trial",
  }),
  publicRoute(
    URLs.register_email_verified,
    () => import("@/pages/SignUp/EmailVerified"),
  ),
  publicRoute(
    URLs.reset_password,
    () => import("@/pages/SignUp/ResetPassword"),
  ),
  publicRoute("*", () => import("@/pages/error")),
];

const appRoutes: RouteObject[] = [
  {
    index: true,
    loader: rootRouteLoader,
  },
  // Security Overview: the TrustAI Executive Board replaces the legacy dashboard
  // for tenants on the new agent-onboarding experience. Deliberately not
  // flag-redirected — this path is the default landing target (see
  // `Routes/loaders`), so a denied tenant must still get a page here.
  mockScopedSwapRoute(
    AGENT_BOARD,
    authRoute(URLs.dashboard, () => import("@/pages/TrustAIExecutiveBoard")),
    authRoute(URLs.dashboard, () => import("@/pages/Dashboard")),
  ),
  authRoute(URLs.welcome, () => import("@/pages/Welcome")),
  authRoute(URLs.accounts, () => import("@/pages/Accounts")),
  authRoute(URLs.domain_policy, () => import("@/pages/DomainPolicy")),
  ...accountDetailPaths.map(path =>
    authRoute(path, () => import("@/pages/Accounts/Details")),
  ),
  ...reportsPagePaths.map(path =>
    authRoute(path, () => import("@/pages/Reports/ReportsPage")),
  ),
  authRoute(
    URLs.reports_detail_audit_report,
    () => import("@/pages/Reports/AuditReportPage"),
  ),
  ...userAttributePaths.map(path =>
    authRoute(path, () => import("@/pages/UserAttributes")),
  ),
  ...userReportPaths.map(path =>
    authRoute(path, () => import("@/pages/UserReports")),
  ),
  authRoute(
    URLs.data_classification_admin,
    () => import("@/pages/AdminDataClassification"),
  ),
  authRoute(URLs.users, () => import("@/pages/Users")),
  authRoute(URLs.buckets, () => import("@/pages/Buckets")),
  authRoute(URLs.group_user_mapping, () => import("@/pages/GroupUserMapping")),
  authRoute(URLs.tenant_activation, () => import("@/pages/TenantActivation")),
  authRoute(
    URLs.authentication_security,
    () => import("@/pages/AuthenticationSecurity"),
  ),
  authRoute(URLs.oAuth_activation, () => import("@/pages/OAuth")),
  authRoute(
    URLs.integrations,
    () => import("@/pages/Integrations/Integrations"),
  ),
  authRoute(URLs.administration, () => import("@/pages/Administration")),
  authRoute(
    URLs.administration_data_source_settings,
    () => import("@/pages/Administration/DataSourceSettings"),
  ),
  authRoute(
    URLs.configurations_privilege_template_management,
    () =>
      import(
        "@/pages/Configurations/privilegeTemplateManagement/PrivilegeTemplateManagement"
      ),
  ),
  authRoute(
    URLs.configurations_tenant_settings,
    () => import("@/pages/Configurations/tenantSettings"),
  ),
  ...configUdfPaths.map(path =>
    authRoute(
      path,
      () => import("@/pages/Configurations/user-defined-functions"),
    ),
  ),
  authRoute(
    URLs.configurations_utility_scripts,
    () => import("@/pages/Configurations/utility-scripts"),
  ),
  authRoute(
    URLs.configurations_on_prem_metadata,
    () => import("@/pages/Configurations/onPremMetadata"),
  ),
  authRoute(
    URLs.configurations_risk_threshold,
    () => import("@/pages/Configurations/riskThreshold"),
  ),
  ...featureFlaggedRoutes(
    "isNewAgentOnboarding",
    authRoute(
      URLs.ai_policy_enforcement,
      () => import("@/pages/Administration/AIPolicyEnforcement"),
    ),
  ),
  authRedirectRoute(URLs.legacy_configurations, URLs.administration),
  authRedirectRoute(
    URLs.legacy_configurations_privilege_template_management,
    URLs.configurations_privilege_template_management,
  ),
  authRedirectRoute(
    URLs.legacy_configurations_tenant_settings,
    URLs.configurations_tenant_settings,
  ),
  ...legacyConfigUdfPaths.map(path =>
    redirectToTypedPath(
      path,
      URLs.configurations_user_defined_functions,
      URLs.configurations_user_defined_functions_type,
    ),
  ),
  authRedirectRoute(
    URLs.legacy_configurations_utility_scripts,
    URLs.configurations_utility_scripts,
  ),
  authRedirectRoute(
    URLs.legacy_configurations_on_prem_metadata,
    URLs.configurations_on_prem_metadata,
  ),
  authRedirectRoute(
    URLs.legacy_configurations_risk_threshold,
    URLs.configurations_risk_threshold,
  ),
  authRedirectRoute(URLs.legacy_integrations, URLs.integrations),
  authRedirectRoute(URLs.legacy_users_attributes, URLs.users_attributes),
  redirectToTypedPath(
    URLs.legacy_users_attributes_type,
    URLs.users_attributes,
    URLs.users_attributes_type,
  ),
  authRoute(URLs.domain_management, () => import("@/pages/DomainManagement")),
  ...domainDataDomainPaths.map(path =>
    authRoute(path, () => import("@/pages/DomainManagement/DataDomainPage")),
  ),
  ...domainDataProductPaths.map(path =>
    authRoute(
      path,
      () => import("@/pages/DomainManagement/DataProducts/DataProductPage"),
    ),
  ),
  authRoute(
    URLs.applications,
    () => import("@/pages/ResourcePolicies/ApplicationsPage"),
  ),
  ...resourcePolicyPaths.map(path =>
    authRoute(path, () => import("@/pages/ResourcePolicies")),
  ),
  authRouteWithProps(
    URLs.ai_agents,
    () => import("@/pages/ResourcePolicies/ApplicationsPage"),
    { applicationType: ApplicationType.AI_AGENT },
  ),
  ...mcpRegistryPaths.map(path =>
    authRoute(path, () => import("@/pages/MCPRegistry")),
  ),
  ...mcpServerDetailsPaths.map(path =>
    authRoute(path, () => import("@/pages/MCPRegistry/Details")),
  ),
  ...aiAgentResourcePolicyPaths.map(path =>
    authRouteWithProps(path, () => import("@/pages/ResourcePolicies"), {
      applicationType: ApplicationType.AI_AGENT,
    }),
  ),
  authRoute(URLs.aiLanding, () => import("@/pages/AILanding/AILandingPage")),

  // Scoped click-through previews — gated by a tenant feature flag and
  // rendered under <MockScope>. Inside, useQuery/useMutation route mocked
  // methods through src/api/mock-data; unmocked methods fall through to the
  // real backend.
  // TODO: Remove this once the unified lakehouse feature is integrated.
  ...mockScopedRoutes(
    "isLakeHouseMetadataEnabled",
    ...unifiedLakehousePaths.map(path =>
      authRoute(path, () => import("@/pages/UnifiedLakehouse")),
    ),
  ),
  // TODO: Remove this once the agent estate backend ships and the pages move
  // to the generated client.
  ...mockScopedRoutes(
    AGENT_ONBOARDING,
    authRoute(URLs.agent_estate, () => import("@/pages/AgentEstate")),
    authRoute(URLs.agent_platforms, () => import("@/pages/AgentPlatforms")),
    // Below: pages whose backend has not shipped - demo mode only until their
    // feature goes live in `@/configs/agentFeatures`.
    ...agentFeatureRoutes(
      "agentDetails",
      ...agentEstateDetailPaths.map(path =>
        authRoute(path, () => import("@/pages/AgentEstate/Details")),
      ),
    ),
    // Administration → AI Policy Enforcement → OAuth Client Profiles. The
    // same mock store backs the Authentication Setup client-profile picker.
    ...agentFeatureRoutes(
      "oauthClientProfiles",
      authRoute(
        URLs.oauth_client_profiles,
        () => import("@/pages/Administration/OAuthClientProfiles"),
      ),
    ),
    ...agentFeatureRoutes(
      "relationshipExplorer",
      authRoute(
        URLs.trustai_relationships,
        () => import("@/pages/TrustAIRelationshipExplorer"),
      ),
    ),
    ...agentFeatureRoutes(
      "accessFlows",
      authRoute(
        URLs.trustai_access_flows,
        () => import("@/pages/TrustAIAccessFlows"),
      ),
    ),
  ),
  // Gated by the same flag as the sidebar entry in `configs/menu`. The list
  // route would inherit it from that entry anyway; the detail routes would not,
  // so gate the whole section explicitly.
  ...featureFlaggedRoutes(
    "isEnterprisePolicyEnabled",
    authRoute(
      URLs.enterprise_policies,
      () => import("@/pages/EnterprisePolicies"),
    ),
    ...enterprisePolicyDetailPaths.map(path =>
      authRoute(path, () => import("@/pages/EnterprisePolicies/Details")),
    ),
  ),
];

// Route configuration
export const routes: RouteObject[] = [
  // Root route with App component for initialization
  {
    path: "/",
    errorElement: <LazyLoadErrorFallback />,
    Component: App,
    children: [
      // Root index - redirects to default landing path
      {
        index: true,
        loader: rootRouteLoader,
      },
      // Public routes
      ...publicRoutes,
    ],
  },

  // Authenticated app routes
  {
    path: "/app",
    loader: requireAuthLoader,
    errorElement: <LazyLoadErrorFallback />,
    Component: Layout,
    children: appRoutes,
  },

  // Copilot routes (authenticated)
  {
    path: "/copilot",
    loader: requireAuthLoader,
    Component: () => (
      <Suspense fallback={<Loading isLoading={true} />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: replaceFirstSlash(URLs.copilotUI),
        ...createLazyRoute(() => import("@/components/Copilot/WidgetRoute")),
      },
    ],
  },
];
